import styled, { css, keyframes } from 'styled-components';
import { screenLg } from 'constants/styles/breakpoints';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Used by SpinnerIcon in ExportButton.tsx. color: var(--lilac) flows into fill="currentColor".
export const SpinSvg = styled.svg`
  color: var(--lilac);
  animation: ${spin} 1.4s linear infinite;
  flex-shrink: 0;
`;

/* Secondary (default): light fill, lilac border — Phase 1 Export chart/table. */
const secondaryVariant = css`
  .ant-btn:not([disabled]):hover {
    box-shadow: none !important;
    border-color: var(--black-dark) !important;
  }

  .ant-btn:not([disabled]):active {
    background-color: var(--black-dark) !important;
    border-color: var(--black-dark) !important;
    color: var(--white) !important;
    box-shadow: none !important;
  }

  &[data-loading] .ant-btn[disabled] {
    opacity: 1 !important;
    border-color: var(--lilac) !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
  }
`;

/* Primary (Export page): dark fill, white label, lilac icon — Phase 2. */
const primaryVariant = css`
  .ant-btn {
    background-color: var(--black-dark) !important;
    border-color: var(--black-dark) !important;
    color: var(--white) !important;
    transition: none !important;
  }
  .ant-btn svg {
    color: var(--lilac) !important;
  }

  .ant-btn:not([disabled]):hover {
    background-color: var(--black-dark) !important;
    border-color: var(--black-dark) !important;
    color: var(--white) !important;
    box-shadow: 0px 4px 20px rgba(18, 18, 18, 0.45) !important;
  }

  .ant-btn:not([disabled]):active {
    background-color: var(--grey-light) !important;
    border-color: var(--grey-light) !important;
    color: var(--white) !important;
    box-shadow: none !important;
  }

  /* Lavender border + subtle shadow while the menu is open */
  .ant-btn[aria-expanded='true'],
  .ant-btn[aria-expanded='true']:hover {
    border-color: var(--lilac) !important;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25) !important;
  }

  &:not([data-loading]) .ant-btn[disabled] {
    background-color: var(--light-gray-darker) !important;
    border-color: var(--lilac) !important;
    color: var(--white) !important;
    opacity: 0.25 !important;
    cursor: not-allowed !important;
  }

  &[data-loading] .ant-btn[disabled] {
    background-color: var(--black-dark) !important;
    border-color: var(--lilac) !important;
    color: var(--white) !important;
    opacity: 1 !important;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25) !important;
  }
  &[data-loading] .ant-btn svg {
    width: 22px !important;
    height: 22px !important;
  }
`;

/* Shrinks to the Figma tablet/mobile spec below lg, full size from lg up. Used
   by the page export button, which sits in the filter bar alongside controls
   that reflow at the same breakpoint. */
const responsiveSize = css`
  .ant-btn {
    padding: 8px 16px !important;
    height: 36px !important;
    font-size: 14px !important;
    line-height: 20px !important;
  }

  ${screenLg} {
    .ant-btn {
      padding: 0 24px !important;
      height: 40px !important;
      font-size: 16px !important;
      line-height: 23px !important;
    }
  }
`;

export const ExportButtonContainer = styled.div<{
  $variant?: 'secondary' | 'primary';
  $size?: 'default' | 'responsive';
}>`
  display: inline-flex;

  /* Button box model and typography */
  .ant-btn {
    font-family: var(--font-family-base) !important;
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 23px !important;
    letter-spacing: 0 !important;
    padding: 0 24px !important;
    gap: 10px !important;
    height: 40px !important;
  }

  /* Kill the antd click ripple (expanding outward shadow) */
  .ant-btn::after {
    display: none !important;
  }

  ${({ $variant }) => ($variant === 'primary' ? primaryVariant : secondaryVariant)}
  ${({ $size }) => $size === 'responsive' && responsiveSize}
`;

export const DropdownMenu = styled.div`
  background: var(--white);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0px 30px 24px rgba(0, 0, 0, 0.2);
  width: max-content;
`;

export const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-family: var(--font-family-base);
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--light-gray-darker);
  transition: background-color 0.15s;

  &:hover,
  &:focus-visible {
    background-color: var(--grey-lighter);
    color: var(--black-dark);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled span {
    opacity: 0.5;
  }
`;
